import { Fragment, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import { Dialog, Transition } from '@headlessui/react'
import Select from 'react-select'
import {
  GetAvailableCollectionSlotsQuery,
  useGetAvailableCollectionSlotsQuery,
} from '../generated/urql.client'
import { useAtom } from 'jotai'
import { selectCollectionSlotModalDataAtom } from '../store'
import { TailSpin } from 'react-loader-spinner'
import { Blue600 } from '../lib/colors'
import { MdDoDisturb } from 'react-icons/md'
import { useI18n } from '../provider/i18n'

type Slot = { from: string; to: string; id: number }
type SelectDayOption = { label: string; value: string }
type SelectSlotOption = { label: string; value: number }

const prepareSlotsData = (
  slots: GetAvailableCollectionSlotsQuery['collection_slot']
) => {
  const tempByDay: Record<string, Slot[]> = {}
  const byDay: Record<string, SelectSlotOption[]> = {}

  for (const s of slots) {
    const start = moment(s.dateFrom)
    const end = moment(s.dateTo)

    if (!start.isValid() || !end.isValid() || !end.isAfter(start)) continue

    let cursorStart = start.clone()
    const finalEnd = end.clone()

    while (cursorStart.isBefore(finalEnd)) {
      const dayKey = cursorStart.format('dddd, DD.MM.YYYY')
      const dayEnd = cursorStart.clone().endOf('day')
      const segmentEnd = moment.min(dayEnd, finalEnd)

      if (!tempByDay[dayKey]) tempByDay[dayKey] = []
      tempByDay[dayKey].push({
        from: cursorStart.format('HH:mm'),
        to: segmentEnd.format('HH:mm'),
        id: s.id,
      })

      cursorStart = dayEnd.clone().add(1, 'millisecond').startOf('day')
    }
  }

  for (const day of Object.keys(tempByDay)) {
    tempByDay[day].sort((a, b) => a.from.localeCompare(b.from))
    const dedup: (typeof byDay)[typeof day] = []
    let prev: (typeof tempByDay)[typeof day][number] | null = null
    for (const cur of tempByDay[day]) {
      if (!prev || prev.from !== cur.from || prev.to !== cur.to)
        dedup.push({ label: `${cur.from} - ${cur.to}`, value: cur.id })
      prev = cur
    }
    byDay[day] = dedup
  }

  const days = Object.keys(byDay)
    .sort((a, b) => {
      const pa = moment(a, 'dddd, DD.MM.YYYY')
      const pb = moment(b, 'dddd, DD.MM.YYYY')
      if (!pa.isValid() || !pb.isValid()) return a.localeCompare(b)
      return pa.valueOf() - pb.valueOf()
    })
    .map((d) => ({ label: d, value: d }))

  return { days, byDay }
}

const SelectCollectionSlotModal = () => {
  const i18n = useI18n()
  const [selectedDay, setSelectedDay] = useState<SelectDayOption>()
  const [selectedSlot, setSelectedSlot] = useState<SelectSlotOption>()
  const [daySlots, setDaySlots] = useState<SelectSlotOption[]>()
  const [
    { isOpen, collectionPointId, minDate, slotId, onSelect },
    set,
  ] = useAtom(selectCollectionSlotModalDataAtom)

  const dateFrom = useMemo(() => minDate || moment().toISOString(), [minDate])

  const [{ fetching, data }] = useGetAvailableCollectionSlotsQuery({
    variables: {
      collectionPointId: collectionPointId || 0,
      dateFrom,
    },
    pause: !collectionPointId,
    requestPolicy: 'network-only',
  })

  const availableSlots = data?.collection_slot || []

  const { days, byDay } = useMemo(
    () => prepareSlotsData(availableSlots),
    [availableSlots]
  )

  useEffect(() => {
    setDaySlots(selectedDay ? byDay[selectedDay.value] : undefined)
  }, [selectedDay, byDay])

  useEffect(() => {
    const canSetDefault =
      !fetching &&
      slotId &&
      availableSlots.length &&
      !selectedDay &&
      !selectedSlot
    if (canSetDefault) {
      const defaultSlot = availableSlots.find((s) => s.id === slotId)
      if (defaultSlot) {
        const from = moment(defaultSlot.dateFrom)
        const to = moment(defaultSlot.dateTo)
        const day = from.format('dddd, DD.MM.YYYY')
        setSelectedDay({ label: day, value: day })
        setSelectedSlot({
          label: `${from.format('HH:mm')} - ${to.format('HH:mm')}`,
          value: defaultSlot.id,
        })
      }
    }
  }, [fetching, availableSlots, slotId, selectedDay, selectedSlot])

  const onClose = () => {
    setSelectedDay(undefined)
    setSelectedSlot(undefined)
    set({ isOpen: false })
  }

  const onSelectDay = (newValue?: SelectDayOption) => {
    setSelectedDay(newValue)
  }

  const onSelectSlot = (newValue?: SelectSlotOption) => {
    setSelectedSlot(newValue)
  }

  const onConfirm = () => {
    if (selectedSlot && onSelect && collectionPointId) {
      const slot = availableSlots.find((slot) => slot.id === selectedSlot.value)
      if (slot) {
        onSelect({
          id: slot.id,
          dateFrom: slot.dateFrom,
          dateTo: slot.dateTo,
          collectionPointId: collectionPointId,
        })
      }
    }
    onClose()
  }

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full sm:max-w-lg transform rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-medium leading-6 text-gray-900"
                >
                  {'Proszę wybrać termin pobrania'}
                </Dialog.Title>
                <div className="mt-4">
                  {fetching ? (
                    <TailSpin
                      wrapperClass="flex justify-center"
                      color={Blue600}
                      width={32}
                      height={32}
                    />
                  ) : !days.length ? (
                    <div className="flex flex-col items-center gap-1 text-gray-500 text-base">
                      <MdDoDisturb size={32} />
                      {i18n.t('components.datebutton.selectDateEmpty')}
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2">
                      <Select
                        value={selectedDay}
                        options={days}
                        placeholder={i18n.t(
                          'components.datebutton.selectDateDay'
                        )}
                        isDisabled={!days.length}
                        onChange={(a) =>
                          onSelectDay(
                            a ? { label: a.label, value: a.value } : undefined
                          )
                        }
                      />
                      <Select
                        value={selectedSlot}
                        options={daySlots}
                        placeholder={i18n.t(
                          'components.datebutton.selectDateSlot'
                        )}
                        isDisabled={!daySlots}
                        onChange={(a) =>
                          onSelectSlot(
                            a ? { label: a.label, value: a.value } : undefined
                          )
                        }
                      />
                    </div>
                  )}
                </div>

                <div className="flex flex-row gap-2 justify-end mt-4">
                  <button
                    type="button"
                    className="hl-button hl-gradient-red"
                    onClick={onClose}
                  >
                    {i18n.t('common.cancel')}
                  </button>
                  <button
                    type="button"
                    className="hl-button"
                    disabled={!(selectedDay && selectedSlot)}
                    onClick={onConfirm}
                  >
                    {i18n.t('common.confirm')}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

export default SelectCollectionSlotModal

